import React, { FC } from 'react';
import { createUseStyles } from 'react-jss';
import { Button, Icon } from '@packages/ui/shared';
import { getSubaruDiagnosticsUrl } from '@web/utils/can-view-stis';
import type { StisAccessState } from '@web/utils/can-view-stis';

interface StisAccessBlockedViewProps {
    state: Extract<StisAccessState, 'EXPIRED' | 'PENDING' | 'NO_SUBSCRIPTION'>;
}

const useStyles = createUseStyles({
    skeletonWrapper: {
        position: 'absolute',
        inset: 0,
        filter: 'blur(4px)',
        pointerEvents: 'none',
        userSelect: 'none',
        overflow: 'hidden',
        padding: '24px 32px',
    },
    skeletonBar: {
        borderRadius: 6,
        backgroundColor: '#e8eaed',
        marginBottom: 12,
    },
    skeletonTitle: {
        height: 20,
        width: '40%',
        marginBottom: 20,
    },
    skeletonRow: {
        height: 14,
        marginBottom: 10,
    },
    skeletonRowShort: {
        width: '55%',
    },
    skeletonRowMedium: {
        width: '75%',
    },
    skeletonRowFull: {
        width: '90%',
    },
    skeletonDivider: {
        height: 1,
        backgroundColor: '#e8eaed',
        margin: '20px 0',
    },

    overlay: {
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.72)',
        backdropFilter: 'blur(2px)',
        padding: 32,
        zIndex: 10,
    },
    lockCircle: {
        width: 72,
        height: 72,
        borderRadius: '50%',
        backgroundColor: '#f0f2f5',
        border: '2px solid #d8dce2',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 24,
    },
    title: {
        fontFamily: 'Roboto',
        fontSize: 20,
        fontWeight: 700,
        color: '#071424',
        textAlign: 'center',
        marginBottom: 8,
        lineHeight: '140%',
    },
    message: {
        fontFamily: 'Roboto',
        fontSize: 15,
        fontWeight: 400,
        color: '#4C525D',
        textAlign: 'center',
        maxWidth: 420,
        lineHeight: '160%',
        marginBottom: 28,
    },

    container: {
        position: 'relative',
        minHeight: 'calc(100vh - 120px)',
        width: '100%',
    },
});

const SkeletonRows: FC<{ classes: ReturnType<typeof useStyles> }> = ({ classes }) => (
    <>
        {[
            classes.skeletonRowFull,
            classes.skeletonRowMedium,
            classes.skeletonRowFull,
            classes.skeletonRowShort,
            classes.skeletonRowMedium,
            classes.skeletonRowFull,
            classes.skeletonRowShort,
        ].map((widthClass, i) => (
            <div key={i} className={`${classes.skeletonBar} ${classes.skeletonRow} ${widthClass}`} />
        ))}
    </>
);

export const StisAccessBlockedView: FC<StisAccessBlockedViewProps> = ({ state }) => {
    const classes = useStyles();

    const isExpired = state === 'EXPIRED';
    const isPending = state === 'PENDING';

    const title = isExpired
        ? 'Your STIS subscription has expired'
        : isPending
          ? 'Subscription payment pending'
          : 'STIS subscription required';

    const message = isExpired
        ? 'Your access to STIS publications has been revoked. Renew your subscription to restore access.'
        : isPending
          ? 'Your subscription payment is being processed. Access to STIS publications will be granted once payment is confirmed.'
          : 'A valid STIS subscription is required to access this content. Purchase a subscription to get started.';

    const ctaLabel = isExpired ? 'Renew Subscription' : 'Purchase Subscription';

    return (
        <div className={classes.container}>
            <div className={classes.skeletonWrapper} aria-hidden="true">
                <div className={`${classes.skeletonBar} ${classes.skeletonTitle}`} />
                <SkeletonRows classes={classes} />
                <div className={classes.skeletonDivider} />
                <SkeletonRows classes={classes} />
            </div>

            <div className={classes.overlay} role="status" aria-live="polite">
                <div className={classes.lockCircle}>
                    <Icon name={isPending ? 'clock' : 'lock-closed'} size={32} color="graySix" />
                </div>
                <p className={classes.title}>{title}</p>
                <p className={classes.message}>{message}</p>
                {isPending ? (
                    <Button
                        variant="primary"
                        onPress={() => window.open(getSubaruDiagnosticsUrl(), '_blank', 'noopener,noreferrer')}
                        iconRight={<Icon name="open-in-new" size={14} color="white" />}
                    >
                        View Status
                    </Button>
                ) : (
                    <Button
                        variant="primary"
                        onPress={() => window.open(getSubaruDiagnosticsUrl(), '_blank', 'noopener,noreferrer')}
                        iconRight={<Icon name="open-in-new" size={14} color="white" />}
                    >
                        {ctaLabel}
                    </Button>
                )}
            </div>
        </div>
    );
};
